import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { SaveRate, ServiceRate } from '@spike/boarding-rule-model';
import { Rate } from './Form/types';
import {
    RatesStatus,
    fetchRatesThunk,
    saveRateThunk,
    deleteRateThunk,
    fetchRatesByServiceIdThunk
} from '@spike/boarding-rules-action';
import { fetchServicesThunk } from 'actions/services/ServicesActions';
import { showSuccessThunk, showErrorThunk } from '@spike/notifications-action';
import { useApiClientWrapper } from 'hooks';
import { RootState } from 'store';
import { Service } from 'model/Service';
import moment from 'moment-timezone';

interface BoardingRatesContextProps {
    // Data
    rates: Rate[];
    isLoading: boolean;
    isFirstRender: boolean;
    hasError: boolean;

    // Services data (from existing Redux)
    boardingServices: Service[];
    areServicesLoading: boolean;
    allServiceRates: ServiceRate[];

    // Marketplace ID for save operations
    marketplaceId: number;

    // Filter state
    selectedFilter: string;
    filterChangeHandler: (filter: string) => void;
    isTrueEmpty: boolean;
    isFilteredEmpty: boolean;

    // Form state
    showForm: boolean;
    selectedRate: Rate | null;
    editServiceId: number | null;
    openAddForm: () => void;
    openEditForm: (rate: Rate, serviceId?: number) => void;
    closeForm: () => void;

    // Actions (saveRate receives SaveRate directly - no intermediate types)
    saveRate: (saveRateData: SaveRate) => Promise<void>;
    deleteRate: (rateId: number) => Promise<void>;
    toggleActive: (rateId: number) => Promise<void>;
    fetchRatesByServiceId: (serviceId: number) => Promise<void>;

    // Per-service loading state
    isServiceRatesLoading: (serviceId: number) => boolean;
    areAllServiceRatesLoaded: boolean;

    // Shared-rate helpers
    isRateSharedWithOtherServices: (rateId: number, serviceId: number) => boolean;
    splitAndSaveRate: (createPayload: SaveRate, destroyPayload: SaveRate, serviceId: number) => Promise<void>;
    deleteServiceFromRate: (serviceRate: ServiceRate) => Promise<void>;
    splitToggleActive: (serviceRate: ServiceRate, newActive: boolean) => Promise<void>;
}

const BoardingRatesContext = React.createContext<BoardingRatesContextProps | null>(null);

export const BoardingRatesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    // Redux selectors
    const allRates = useSelector((state: RootState) => state.rates.rates);
    const ratesStatus = useSelector((state: RootState) => state.rates.status);
    const marketplaceId = useSelector((state: RootState) => state.login.auth?.marketplaceId || 0);
    const boardingServices = useSelector(
        (state: RootState) =>
            state.services.servicesByBusinessArea?.boarding?.services?.filter(s => s.status?.id === 1) || []
    );
    const servicesLoading = useSelector((state: RootState) => state.services.loading);
    const allServiceRates = useSelector((state: RootState) => state.rates.serviceRates);

    // Local state
    const [loading, setLoading] = React.useState({ rates: true, initialLoading: true });
    const [showForm, setShowForm] = React.useState(false);
    const [selectedRate, setSelectedRate] = React.useState<Rate | null>(null);
    const [editServiceId, setEditServiceId] = React.useState<number | null>(null);
    const [pageInfo, setPageInfo] = React.useState({ status: 'active' });

    // Per-service loading state
    const [loadingServiceIds, setLoadingServiceIds] = React.useState<Set<number>>(new Set());
    const [loadedServiceIds, setLoadedServiceIds] = React.useState<Set<number>>(new Set());

    // Fetch guards
    const hasFetchedRatesRef = React.useRef(false);
    const hasFetchedServicesRef = React.useRef(false);
    const hasFetchedAllServiceRatesRef = React.useRef(false);

    // Fetch rates on mount
    React.useEffect(() => {
        if (!hasFetchedRatesRef.current) {
            hasFetchedRatesRef.current = true;
            dispatch(fetchRatesThunk(apiClientWrapper));
        }
    }, [dispatch, apiClientWrapper]);

    // Fetch all boarding services on mount
    React.useEffect(() => {
        if (hasFetchedServicesRef.current) return;
        hasFetchedServicesRef.current = true;

        const ALL_SERVICES_PER_PAGE = 1000;
        dispatch(
            fetchServicesThunk(undefined, undefined, ALL_SERVICES_PER_PAGE, 'active', undefined, undefined, 'boarding')
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Background fetch: load all service rates in parallel on mount
    React.useEffect(() => {
        if (hasFetchedAllServiceRatesRef.current || boardingServices.length === 0) return;
        hasFetchedAllServiceRatesRef.current = true;

        const serviceIds = boardingServices.map(s => s.id!).filter(Boolean);
        setLoadingServiceIds(new Set(serviceIds));

        serviceIds.forEach(serviceId => {
            (dispatch as any)(fetchRatesByServiceIdThunk(apiClientWrapper, serviceId)).finally(() => {
                setLoadingServiceIds(prev => {
                    const next = new Set(prev);
                    next.delete(serviceId);
                    return next;
                });
                setLoadedServiceIds(prev => new Set(Array.from(prev).concat(serviceId)));
            });
        });
    }, [boardingServices.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // Update loading state based on status
    React.useEffect(() => {
        if (
            ratesStatus === RatesStatus.FetchSuccess ||
            ratesStatus === RatesStatus.Error ||
            ratesStatus === RatesStatus.SaveSuccess
        ) {
            setLoading(prev => ({ ...prev, rates: false, initialLoading: false }));
        }
        if (ratesStatus === RatesStatus.Fetching || ratesStatus === RatesStatus.Saving) {
            setLoading(prev => ({ ...prev, rates: true }));
        }
    }, [ratesStatus]);

    // Handle errors
    /*    React.useEffect(() => {
        if (ratesStatus === RatesStatus.Error) {
            dispatch(showErrorThunk('Failed to load rates'));
        }
    }, [ratesStatus, dispatch]); */

    // Local filtering
    const rates = React.useMemo(() => {
        const now = moment();
        if (pageInfo.status === 'active') {
            return allRates.filter(rate => rate.active && !now.isAfter(rate.endDate));
        }
        return allRates.filter(rate => !rate.active || now.isAfter(rate.endDate));
    }, [allRates, pageInfo.status]);

    // Empty state flags
    const isTrueEmpty = allRates.length === 0;
    const isFilteredEmpty = !isTrueEmpty && rates.length === 0;

    // Derived: all service rates loaded
    const areAllServiceRatesLoaded =
        boardingServices.length > 0 && loadedServiceIds.size >= boardingServices.length && loadingServiceIds.size === 0;

    // Form handlers
    const openAddForm = React.useCallback(() => {
        setSelectedRate(null);
        setShowForm(true);
    }, []);

    const openEditForm = React.useCallback((rate: Rate, serviceId?: number) => {
        setSelectedRate(rate);
        setEditServiceId(serviceId ?? null);
        setShowForm(true);
    }, []);

    const closeForm = React.useCallback(() => {
        setShowForm(false);
        setSelectedRate(null);
        setEditServiceId(null);
    }, []);

    // Actions - saveRate receives SaveRate directly (no intermediate transformation)
    const saveRate = React.useCallback(
        async (saveRateData: SaveRate): Promise<void> => {
            await (dispatch as any)(saveRateThunk(apiClientWrapper, saveRateData));
            dispatch(showSuccessThunk(saveRateData.id ? 'Rate updated' : 'Rate created'));
        },
        [dispatch, apiClientWrapper]
    );

    const deleteRate = React.useCallback(
        async (rateId: number): Promise<void> => {
            await (dispatch as any)(deleteRateThunk(apiClientWrapper, rateId));
            dispatch(showSuccessThunk('Rate deleted'));
        },
        [dispatch, apiClientWrapper]
    );

    const toggleActive = React.useCallback(
        async (rateId: number): Promise<void> => {
            const rate = allRates.find(r => r.id === rateId);
            if (rate) {
                const saveRateData: SaveRate = {
                    id: rate.id,
                    name: crypto.randomUUID(),
                    marketplaceId: rate.marketplaceId,
                    startDate: rate.startDate,
                    endDate: rate.endDate,
                    minNights: rate.minNights,
                    active: !rate.active,
                    customerTagList: rate.customerTagList,
                    rateItemsAttributes:
                        (rate as Rate).rateItems?.map(item => ({
                            id: item.id,
                            marketplaceId: item.marketplaceId,
                            itemId: item.itemId,
                            itemType: item.itemType,
                            quantity: item.quantity,
                            amount: item.amount
                        })) || []
                };
                try {
                    await (dispatch as any)(saveRateThunk(apiClientWrapper, saveRateData));
                    dispatch(showSuccessThunk(rate.active ? 'Rate deactivated' : 'Rate activated'));
                } catch {
                    const message =
                        (dispatch as any)((_: unknown, getState: () => RootState) => getState().rates.error) ??
                        'Failed to update rate';
                    dispatch(showErrorThunk(message));
                }
            }
        },
        [dispatch, apiClientWrapper, allRates]
    );

    const filterChangeHandler = React.useCallback((filter: string) => {
        setPageInfo({ status: filter });
    }, []);

    const fetchRatesByServiceId = React.useCallback(
        async (serviceId: number) => {
            try {
                await (dispatch as any)(fetchRatesByServiceIdThunk(apiClientWrapper, serviceId));
            } catch (error) {
                (dispatch as any)(showErrorThunk('Failed to load rates for service'));
            }
        },
        [dispatch, apiClientWrapper]
    );

    // Per-service loading check
    const isServiceRatesLoading = React.useCallback(
        (serviceId: number): boolean => loadingServiceIds.has(serviceId),
        [loadingServiceIds]
    );

    // Check if a rate is shared with other services (beyond the given serviceId)
    const isRateSharedWithOtherServices = React.useCallback(
        (rateId: number, serviceId: number): boolean =>
            allServiceRates.some(sr => sr.rate.id === rateId && sr.rateItem.itemId !== serviceId),
        [allServiceRates]
    );

    // Edit-split: create new rate for this service + detach from original
    const splitAndSaveRate = React.useCallback(
        async (createPayload: SaveRate, destroyPayload: SaveRate, serviceId: number): Promise<void> => {
            await (dispatch as any)(saveRateThunk(apiClientWrapper, createPayload));
            if (destroyPayload.rateItemsAttributes.length > 0) {
                await (dispatch as any)(saveRateThunk(apiClientWrapper, destroyPayload));
            }
            await (dispatch as any)(fetchRatesByServiceIdThunk(apiClientWrapper, serviceId));
            dispatch(showSuccessThunk('Rate updated'));
        },
        [dispatch, apiClientWrapper]
    );

    // Delete-shared: detach this service's item from the shared rate
    const deleteServiceFromRate = React.useCallback(
        async (serviceRate: ServiceRate): Promise<void> => {
            try {
                const { rate, rateItem } = serviceRate;
                await (dispatch as any)(
                    saveRateThunk(apiClientWrapper, {
                        id: rate.id,
                        name: crypto.randomUUID(),
                        marketplaceId: rate.marketplaceId,
                        startDate: rate.startDate,
                        endDate: rate.endDate,
                        minNights: rate.minNights,
                        active: rate.active,
                        customerTagList: [...rate.customerTagList],
                        rateItemsAttributes: [{ id: rateItem.id, _destroy: true }]
                    })
                );
                await (dispatch as any)(fetchRatesByServiceIdThunk(apiClientWrapper, rateItem.itemId));
                dispatch(showSuccessThunk('Rate deleted'));
            } catch {
                dispatch(showErrorThunk('Failed to delete rate'));
            }
        },
        [dispatch, apiClientWrapper]
    );

    // Toggle-shared: create new rate with toggled active + detach from original
    const splitToggleActive = React.useCallback(
        async (serviceRate: ServiceRate, newActive: boolean): Promise<void> => {
            try {
                const { rate, rateItem } = serviceRate;
                // POST: new rate for this service with toggled active
                await (dispatch as any)(
                    saveRateThunk(apiClientWrapper, {
                        name: crypto.randomUUID(),
                        marketplaceId: rate.marketplaceId,
                        startDate: rate.startDate,
                        endDate: rate.endDate,
                        minNights: rate.minNights,
                        active: newActive,
                        customerTagList: [...rate.customerTagList],
                        rateItemsAttributes: [
                            {
                                marketplaceId: rateItem.marketplaceId,
                                itemId: rateItem.itemId,
                                itemType: rateItem.itemType,
                                quantity: rateItem.quantity,
                                amount: rateItem.amount
                            }
                        ]
                    })
                );
                // PATCH: detach from original rate
                await (dispatch as any)(
                    saveRateThunk(apiClientWrapper, {
                        id: rate.id,
                        name: crypto.randomUUID(),
                        marketplaceId: rate.marketplaceId,
                        startDate: rate.startDate,
                        endDate: rate.endDate,
                        minNights: rate.minNights,
                        active: rate.active,
                        customerTagList: [...rate.customerTagList],
                        rateItemsAttributes: [{ id: rateItem.id, _destroy: true }]
                    })
                );
                await (dispatch as any)(fetchRatesByServiceIdThunk(apiClientWrapper, rateItem.itemId));
                dispatch(showSuccessThunk(newActive ? 'Rate activated' : 'Rate deactivated'));
            } catch {
                const message =
                    (dispatch as any)((_: unknown, getState: () => RootState) => getState().rates.error) ??
                    'Failed to update rate';
                dispatch(showErrorThunk(message));
            }
        },
        [dispatch, apiClientWrapper]
    );

    const value: BoardingRatesContextProps = {
        rates,
        isLoading: loading.rates,
        isFirstRender: loading.initialLoading,
        hasError: ratesStatus === RatesStatus.Error,
        boardingServices,
        areServicesLoading: servicesLoading,
        allServiceRates,
        marketplaceId,
        selectedFilter: pageInfo.status,
        filterChangeHandler,
        isTrueEmpty,
        isFilteredEmpty,
        showForm,
        selectedRate,
        editServiceId,
        openAddForm,
        openEditForm,
        closeForm,
        saveRate,
        deleteRate,
        toggleActive,
        fetchRatesByServiceId,
        isServiceRatesLoading,
        areAllServiceRatesLoaded,
        isRateSharedWithOtherServices,
        splitAndSaveRate,
        deleteServiceFromRate,
        splitToggleActive
    };

    return <BoardingRatesContext.Provider value={value}>{children}</BoardingRatesContext.Provider>;
};

export const useBoardingRates = (): BoardingRatesContextProps => {
    const context = React.useContext(BoardingRatesContext);
    if (!context) {
        throw new Error('useBoardingRates must be used within a BoardingRatesProvider');
    }
    return context;
};
